// Utility functions for the Asteroids game

/**
 * Scale a value based on canvas size
 */
export function getScaledValue(
  value: number,
  canvasWidth: number,
  canvasHeight: number
): number {
  const scaleFactor = Math.min(canvasWidth / 800, canvasHeight / 600);
  return value * scaleFactor;
}

/**
 * Check if objects are colliding based on their positions and radii
 */
export function checkCollision(
  x1: number,
  y1: number,
  r1: number,
  x2: number,
  y2: number,
  r2: number
): boolean {
  const dx = x1 - x2;
  const dy = y1 - y2;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  return distance < r1 + r2;
}

/**
 * Wrap position around canvas edges
 */
export function wrapPosition(
  position: number,
  max: number
): number {
  if (position < 0) return max;
  if (position > max) return 0;
  return position;
}

/**
 * Throttle function to limit how often a function can be called
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  
  return function(this: any, ...args: Parameters<T>): void {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Debounce function for preventing functions from running too frequently
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(this: any, ...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func.apply(this, args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
